// DISPLAY HEURISTIC ONLY — NOT a counting engine. Points (deity-progress.ts +
// the gameSettings ledger) are the one engine (Master Book ruling 1.8); this
// 0–10 "how ordered right now" score never feeds levels, acquisition, Aim/Goal
// or the STAGE ladder, and must be labelled as an estimate wherever it shows.
// The "pts" below are internal weights of the heuristic, not game points.
//
// ORDER skill scoring — every active tool contributes a 0–10 "skill" score to
// its section; a section's score is the mean of its active tools' scores; the
// overall Order score (how ordered the user's mind is, 0–10) is the mean of
// the five section scores. Tools with no live signal yet return null and are
// excluded from the mean (a section of only-null tools shows "–").
//
// Heuristics are v1 — each draws on the signals useTodayData already loads
// (ASTRA drift, FLO charge/load/flow, agenda, journal, tick queue) so the
// dashboard needs no extra queries.
import { Gear } from '../domain/olmps';
import type { TodayData } from './useTodayData';
import { enabledToolsFor, type SyqiSections, ORDER_SECTIONS } from './orderSections';

function clamp10(n: number): number {
  return Math.max(0, Math.min(10, n));
}

/** 0–10 score for one tool, or null when there's no signal for it yet. */
export function computeToolScore(toolId: string, data: TodayData): number | null {
  const flo = data.flo;
  switch (toolId) {
    // Direction-keeping: fewer sectors off trajectory = better oriented.
    case 'telos':
    case 'astrs':
    case 'oracle':
      return clamp10(10 - data.driftSectors.length * 2.5);
    // Journal written (6 weight) + tick/reschedule queue cleared (4 weight).
    case 'clio': {
      const queuePts = clamp10(4 - Math.min(4, data.openTonightCount / 5));
      return clamp10((data.journalWrittenToday ? 6 : 0) + queuePts);
    }
    // Charge is already on a 0–10 battery scale.
    case 'numa':
      return flo ? clamp10(flo.charge) : null;
    // Flow report is 0–100.
    case 'moyri':
    case 'argos':
      return flo ? clamp10(flo.flow.score / 10) : null;
    // Task load: sweet spot around 50; overload and emptiness both cost.
    case 'kyrs':
      return flo ? clamp10(10 - Math.abs(flo.load - 50) / 8) : null;
    // Agenda structure: having a planned day, and being inside it.
    case 'metis':
      return clamp10((data.totalItemsToday > 0 ? 6 : 2) + (data.currentItem ? 3 : 0) + (data.nextItem ? 1 : 0));
    // Calendar shape: anything actually scheduled today.
    case 'krnos':
      return clamp10(data.dayEntries.length === 0 ? 3 : 6 + Math.min(4, data.dayEntries.length / 2));
    // Execution rhythm: FLO's cycle + steadiness components (each 0–100).
    case 'tkne':
    case 'themis':
    case 'epic':
      return flo ? clamp10((flo.flow.cycle + flo.flow.steadiness) / 20) : null;
    default:
      return null;
  }
}

export interface SectionScore {
  gear: Gear;
  /** Mean of active tools' non-null scores; null when no tool has signal. */
  score: number | null;
  perTool: Record<string, number | null>;
}

export function computeSectionScore(gear: Gear, sections: SyqiSections, data: TodayData): SectionScore {
  const tools = enabledToolsFor(sections, gear);
  const perTool: Record<string, number | null> = {};
  const live: number[] = [];
  for (const toolId of tools) {
    const s = computeToolScore(toolId, data);
    perTool[toolId] = s;
    if (s !== null) live.push(s);
  }
  const score = live.length ? live.reduce((a, b) => a + b, 0) / live.length : null;
  return { gear, score, perTool };
}

export interface OrderScores {
  bySection: Record<Gear, SectionScore>;
  /** Overall Order score 0–10 — mean of non-null section scores. */
  order: number | null;
}

export function computeOrderScores(sections: SyqiSections, data: TodayData): OrderScores {
  const bySection = {} as Record<Gear, SectionScore>;
  const live: number[] = [];
  for (const gear of ORDER_SECTIONS) {
    const s = computeSectionScore(gear, sections, data);
    bySection[gear] = s;
    if (s.score !== null) live.push(s.score);
  }
  return { bySection, order: live.length ? live.reduce((a, b) => a + b, 0) / live.length : null };
}

/** Display helper: "7" (rounded) or "–" when no signal. */
export function formatScore(score: number | null): string {
  return score === null ? '–' : String(Math.round(score));
}
